import { Button, Skeleton, Tooltip } from '@mantine/core';
import { IconDownload, IconEdit, IconFileText, IconPrinter, IconTrash } from '@tabler/icons-react';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuScope } from '../../menu.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Documents.module.css';
import { documentHeading, revisionText } from './library.js';
import { openRevision } from './office.js';
import type { DocumentPermissions } from './source.js';
import type { LibraryDetail, LibrarySection } from './types.js';
import { ownerBadge } from './vessel-library.js';

export interface DocumentHeaderProps {
  section: LibrarySection;
  scope: MenuScope;
  /** `null`: yükleniyor ya da seçim yok. */
  detail: LibraryDetail | null;
  permissions: DocumentPermissions;
  /** Önizlenen revizyon indirilip basılabilir mi (gemide yalnız yürürlükteki; #87). */
  canOutput: boolean;
  /** Pasif Download/Print'in nedeni (ipucu ve ekran okuyucu; inceleme M8a3-R13). */
  outputHint?: string | null | undefined;
  /** Veri bekleniyor: iskelet. Değilse ve doküman yoksa bölüm adı (inceleme M8a3-R5). */
  loading?: boolean | undefined;
  /**
   * "New revision" çizilir mi: yalnız web ofiste şirket belgesi (`canEditDocument`; ADR 0081).
   * Verilmezse eski kural: web ofiste `documents.create`.
   */
  canEdit?: boolean | undefined;
  /**
   * `canEdit` yokken düğme pasif çizilir ve revizyonu kimin hazırladığı ipucunda yazar (ofiste
   * onaylayan DPA; ortak revizyonun yerini bulamadı, ADR 0081). Verilmezse düğme hiç çizilmez.
   */
  revisionHint?: string | null | undefined;
  /** Edit pasif: belge emekli klasörde (ADR 0038 §9; önce canlı klasöre taşınır). */
  editBlocked?: 'retired' | null | undefined;
  /** Birleşik kütüphane (gemide, ofiste gemi kütüphanesi): şirket belgesi "Company" rozetli. */
  unified?: boolean | undefined;
  onEdit: () => void;
  /** "Remove document" (ADR 0142 §5): yalnız web ofiste şirket dokümanında, yetkiliyse. */
  onRemove?: (() => void) | undefined;
  onDownload: () => void;
  onPrint: () => void;
  /**
   * Gemide ISM formunda Download'dan önce tek birincil eylem (`useLibraryFormActions().primary`;
   * ADR 0074): Word/Excel'de "Fill in", PDF'te "Print" ve "Upload signed form". Yoksa başlık
   * tasarımdaki gibidir.
   */
  primaryAction?: ReactNode;
  /**
   * Birincil eylem kayıt açan Print'i taşıyor (PDF formu; ADR 0074): boş kopya basan ikinci Print
   * çizilmez (boş kopya önizleyicinin yazıcı düğmesindedir).
   */
  replacesPrint?: boolean | undefined;
}

/**
 * Başlık kartı (gemi-ism-formlari.jpeg): mavi karoda doküman ikonu, "Form 01 - …" başlığı, alt
 * satırda bölüm ve klasör yolu; sağda Edit (yalnız web ofiste ve `documents.create`; gemide hiç
 * çizilmez, plan "Müşteri ekranından farklar"), Download ve Print (`documents.export`).
 */
export function DocumentHeader({
  section,
  scope,
  detail,
  permissions,
  canOutput,
  outputHint = null,
  loading = detail === null,
  canEdit = scope === 'web' && permissions.create,
  revisionHint = null,
  editBlocked = null,
  unified = false,
  onEdit,
  onRemove,
  onDownload,
  onPrint,
  primaryAction = null,
  replacesPrint = false,
}: DocumentHeaderProps) {
  const { t } = useTranslation();
  const hintId = useId();
  const outputId = useId();
  // Nedeni yalnız pasif düğmede; odaklanamayan ipucunun yanında ekran okuyucuya da (M8a3-R13)
  const outputReason = detail && !canOutput ? outputHint : null;
  const heading = detail ? documentHeading(detail) : '';
  // Dalda açık revizyon varken ikinci taslak açılamaz (OPEN_REVISION_EXISTS): Edit pasif ve
  // nedeni yazılı (inceleme WF-2)
  const open = canEdit && detail ? openRevision(detail) : null;
  const blocked = !canEdit
    ? revisionHint
    : editBlocked === 'retired'
      ? t('documents.library.retiredFolder')
      : open
        ? t('documents.head.editBlocked', { revision: revisionText(open.revisionNo) })
        : null;
  const company = detail ? ownerBadge(detail, unified) === 'company' : false;
  const path = detail
    ? [t(`documents.sections.${section}.title`), ...detail.folderPath.map((f) => f.name)].join(
        ' - ',
      )
    : '';
  const outputButton = (label: string, icon: ReactNode, onClick: () => void) => {
    const button = (
      <Button
        variant="default"
        className={classes.headButton}
        leftSection={icon}
        disabled={!detail || !canOutput}
        aria-describedby={outputReason ? outputId : undefined}
        onClick={onClick}
      >
        {label}
      </Button>
    );
    return outputReason ? (
      <Tooltip label={outputReason} withArrow>
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <section
      className={classes.head}
      aria-label={heading || t(`documents.sections.${section}.details`)}
    >
      <span className={classes.headIcon} aria-hidden>
        <IconFileText size={30} stroke={1.5} />
      </span>
      <div className={classes.headText}>
        {detail ? (
          <>
            <h2 className={classes.headTitle} title={heading}>
              {heading}
            </h2>
            <p className={classes.headPath} title={path}>
              {company ? (
                <span className={classes.headOwner}>
                  <StatusBadge tone="neutral">{t('documents.library.company')}</StatusBadge>
                  <span className={classes.srOnly}>{t('documents.library.companyHint')}</span>
                </span>
              ) : null}
              {path}
            </p>
          </>
        ) : loading ? (
          <>
            <Skeleton height={22} width="55%" />
            <Skeleton height={14} width="35%" mt={10} />
          </>
        ) : (
          <h2 className={classes.headTitle}>{t(`documents.sections.${section}.title`)}</h2>
        )}
      </div>
      <div className={classes.headActions}>
        {onRemove && detail ? (
          <Button
            variant="subtle"
            color="red"
            className={`${classes.headButton} ${classes.headRemove}`}
            leftSection={<IconTrash size={16} />}
            aria-label={t('documents.head.remove')}
            onClick={onRemove}
          >
            <span className={classes.headRemoveLabel}>{t('documents.head.remove')}</span>
          </Button>
        ) : null}
        {canEdit || (detail && revisionHint) ? (
          <Tooltip label={blocked} withArrow disabled={!blocked} multiline maw={320}>
            <span className={classes.headEdit}>
              <Button
                className={classes.headButton}
                leftSection={<IconEdit size={16} />}
                disabled={!detail || Boolean(blocked)}
                aria-describedby={blocked ? hintId : undefined}
                onClick={onEdit}
              >
                {t('documents.head.edit')}
              </Button>
              {blocked ? (
                <span id={hintId} className={classes.srOnly}>
                  {blocked}
                </span>
              ) : null}
            </span>
          </Tooltip>
        ) : null}
        {detail ? primaryAction : null}
        {permissions.export ? (
          <span className={classes.headOutput}>
            {outputButton(t('documents.head.download'), <IconDownload size={16} />, onDownload)}
          </span>
        ) : null}
        {permissions.export && !(detail && primaryAction && replacesPrint) ? (
          <span className={classes.headOutput}>
            {outputButton(t('documents.head.print'), <IconPrinter size={16} />, onPrint)}
          </span>
        ) : null}
        {permissions.export && outputReason ? (
          <span id={outputId} className={classes.srOnly}>
            {outputReason}
          </span>
        ) : null}
      </div>
    </section>
  );
}
